import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Send, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ensure axios sends cookies
axios.defaults.withCredentials = true;

const PROMPT_THEMES = [
  'Self-Compassion',
  'Boundaries',
  'Grief',
  'Self-Trust',
  'Forgiveness',
  'Nervous System',
  'Inner Child',
  'Relationships',
  'Identity',
  'Growth'
];

export const PromptsView = ({ currentPrompt, onPromptCreated }) => {
  const [newPromptText, setNewPromptText] = useState('');
  const [newPromptTheme, setNewPromptTheme] = useState('Self-Compassion');
  const [submittingPrompt, setSubmittingPrompt] = useState(false);

  const handleSubmitPrompt = async () => {
    if (!newPromptText.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setSubmittingPrompt(true);

    try {
      // Cookies are automatically sent with withCredentials: true
      await axios.post(
        `${API}/circle/weekly-prompt`,
        {
          prompt_text: newPromptText,
          theme: newPromptTheme
        }
      );
      
      toast.success('Weekly prompt created!');
      setNewPromptText('');
      onPromptCreated?.();
    } catch (error) {
      toast.error('Failed to create prompt');
    } finally {
      setSubmittingPrompt(false);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <h2
        className="text-2xl sm:text-3xl font-medium"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        Weekly Prompts
      </h2>

      {/* Current Prompt */}
      <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-4 sm:p-6">
        <h3 className="text-lg font-medium text-stone-800 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          Current Active Prompt
        </h3>
        
        {currentPrompt ? (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-stone-700 italic mb-2">"{currentPrompt.prompt_text}"</p>
            <div className="flex gap-4 text-sm text-stone-500">
              <span>Theme: {currentPrompt.theme || 'General'}</span>
              <span>Created: {new Date(currentPrompt.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        ) : (
          <p className="text-stone-500 italic">No active prompt set. Create one below.</p>
        )}
      </div>

      {/* Create New Prompt */}
      <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-4 sm:p-6">
        <h3 className="text-lg font-medium text-stone-800 mb-4">Create New Prompt</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-600 mb-2">
              Prompt Text
            </label>
            <textarea
              value={newPromptText}
              onChange={(e) => setNewPromptText(e.target.value)}
              placeholder="What is your heart asking you to notice this week?"
              className="w-full p-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              rows={3}
              data-testid="prompt-text-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-600 mb-2">
              Theme
            </label>
            <select
              value={newPromptTheme}
              onChange={(e) => setNewPromptTheme(e.target.value)}
              className="w-full p-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="prompt-theme-select"
            >
              {PROMPT_THEMES.map((theme) => (
                <option key={theme} value={theme}>{theme}</option>
              ))}
            </select>
          </div>
          
          <Button
            onClick={handleSubmitPrompt}
            disabled={submittingPrompt || !newPromptText.trim()}
            className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-prompt-button"
          >
            {submittingPrompt ? (
              'Creating...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Publish Prompt
              </>
            )}
          </Button>
          
          <p className="text-xs text-stone-400 text-center">
            This will replace the current active prompt for all Circle members.
          </p>
        </div>
      </div>
    </div>
  );
};
